import * as dateFns from 'date-fns'

import * as common from '../common'

/** Canonical format for displaying date-only values across the application. */
export const FORMAT = 'M/d/yyyy'

/** Values that can be coerced into a date for display. */
export type Input = Date | number | string

interface Parts {
  day: number
  month: number
  year: number
}

/**
 * Formats a date-only value while preserving its intended calendar day across
 * timezones.
 */
export function format(value: Input | null | undefined): string | undefined {
  const date = normalizeToLocal(value)
  if (!date) {
    return
  }

  return dateFns.format(date, FORMAT)
}

/**
 * Normalizes a date-only value to UTC midnight without changing its intended
 * calendar day.
 */
export function normalizeToUtc(value: unknown): Date | undefined {
  const parts = _getParts(value)
  return parts && new Date(Date.UTC(parts.year, parts.month, parts.day))
}

/**
 * Normalizes a date-only value to local midnight without changing its intended
 * calendar day.
 */
export function normalizeToLocal(value: unknown): Date | undefined {
  const parts = _getParts(value)
  return parts && new Date(parts.year, parts.month, parts.day)
}

/** Returns the calendar-day parts represented by a date-only value. */
function _getParts(value: unknown): Parts | undefined {
  const date = common.value.Raw.cast(value, common.value.types.Intrinsic.Date)
  if (!(date instanceof Date)) {
    return
  }

  const useUtcCalendarDay = _isUtcDate(date) && !_isLocalDate(date)
  return {
    day: useUtcCalendarDay ? date.getUTCDate() : date.getDate(),
    month: useUtcCalendarDay ? date.getUTCMonth() : date.getMonth(),
    year: useUtcCalendarDay ? date.getUTCFullYear() : date.getFullYear(),
  }
}

/** Returns whether a date is exactly midnight in UTC. */
function _isUtcDate(value: Date): boolean {
  return (
    value.getUTCHours() === 0 &&
    value.getUTCMinutes() === 0 &&
    value.getUTCSeconds() === 0 &&
    value.getUTCMilliseconds() === 0
  )
}

/** Returns whether a date is exactly midnight in the local timezone. */
function _isLocalDate(value: Date): boolean {
  return (
    value.getHours() === 0 &&
    value.getMinutes() === 0 &&
    value.getSeconds() === 0 &&
    value.getMilliseconds() === 0
  )
}
